/* eslint-disable react-hooks/exhaustive-deps */
import { isBefore, subDays } from "date-fns";
import { useEffect, useState, useMemo, useCallback } from "react";
import { db } from "~/db/db";
import { useGetMapaMina } from "~pages/PriceSearchV2/hooks/usePriceSearchRequests";
import { resetMapa, updateData, updateNaoIds } from "~pages/PriceSearchV2/utils/searchHelpers";

const useMapaMina = ({
  hook,
  clienteId,
  filters,
  pesquisaPlayers,
  updateMapaData,
  isOnline = true,
  forceRefetch = 0,
}) => {
  const [mapasMina, setMapasMina] = useState([]);
  const [mapasMinaOriginais, setMapasMinaOriginais] = useState([]);
  const [loadingMapaRequest, setLoadingMapaRequest] = useState(true);
  const [searchedCount, setSearchedCount] = useState(0);
  const [freeToInitialize, setFreeToInitialize] = useState(false);
  const [oldOrder, setOldOrder] = useState("");
  const [orderedIdsObj, setOrderedIdsObj] = useState({});

  const memoizedFilters = useMemo(
    () => filters,
    [filters?.grupoProduto, filters?.periodo, filters?.produto, filters?.order]
  );

  const memoizedPlayers = useMemo(() => {
    return pesquisaPlayers?.availablePlayers;
  }, [pesquisaPlayers?.availablePlayers]);

  const fetchData = useCallback(async () => {
    if (!memoizedPlayers || typeof memoizedPlayers !== "object") return;
    if (!memoizedFilters || typeof memoizedFilters !== "object" || Object.keys(memoizedFilters)?.length === 0) return;

    const mapas = await queryMapa({ table: db.mapaDaMina, availablePlayersObj: memoizedPlayers, toOrder: true });
    setMapasMina(mapas);

    const mapasOriginais = await queryMapa({ table: db.mapaDaMinaOriginal, availablePlayersObj: memoizedPlayers });
    setMapasMinaOriginais(mapasOriginais);
  }, [memoizedFilters, memoizedPlayers]);

  useEffect(() => {
    if (!freeToInitialize) return;
    fetchData();
  }, [freeToInitialize, fetchData, hook]);

  const getStartDate = (periodo) => {
    const today = new Date();
    switch (periodo) {
      case "trintaDias":
        return subDays(today, 30);
      case "quinzeDias":
        return subDays(today, 15);
      case "seteDias":
        return subDays(today, 7);
      case "hoje":
        today.setHours(0, 0, 0, 0);
        return today;
      default:
        return null;
    }
  };

  const queryMapa = useCallback(
    async ({ table, availablePlayersObj, toOrder = false }) => {
      const { grupoProduto, periodo, produto, order } = memoizedFilters;

      const startDate = getStartDate(periodo);

      let query = table
        .where("clienteId")
        .equals(clienteId)
        .and(
          (item) =>
            (produto ? item.pesquisaProdutoId === produto : true) &&
            (periodo ? isBefore(startDate, new Date(item.createdAt)) : true) &&
            (grupoProduto ? item.pesquisaGrupoMaterialId === grupoProduto : true) &&
            item.deletedAt === undefined &&
            ((Object.entries(availablePlayersObj)?.length && availablePlayersObj[item.pesquisaPlayerId]) ||
              item.clienteFechado ||
              item.clienteNaoConsomeBasicos)
        );

      let results = [];
      await query.each((e) => results.push(e));

      const objValues = Object.values(orderedIdsObj);

      if (!toOrder) {
        return results;
      }

      if (oldOrder !== order || !objValues?.length) {
        setOldOrder(order);

        results = [...results].sort((a, b) =>
          order === "asc"
            ? new Date(a.ultimaAtualizacao) - new Date(b.ultimaAtualizacao)
            : new Date(b.ultimaAtualizacao) - new Date(a.ultimaAtualizacao)
        );

        const obj = {};
        for (const e of results) obj[`id-${e.id}`] = e.id;

        // Salva a ordem dos IDs para consultas futuras
        setOrderedIdsObj(obj);
      } else {
        // Mantém a ordenação original dos IDs armazenados
        const idMap = new Map(results.map((item) => [item.id, item]));
        // Captura os ids novos antes de alterar a variável results
        const newItems = results.filter((item) => !orderedIdsObj[`id-${item.id}`]);

        results = objValues.map((id) => idMap.get(id)).filter(Boolean);

        results = [...results, ...newItems];

        if (newItems?.length) {
          const obj = {};
          for (const e of newItems) {
            if (!orderedIdsObj[`id-${e.id}`]) {
              obj[`id-${e.id}`] = e.id;
            }
          }

          setOrderedIdsObj((prev) => Object.assign({}, prev, obj));
        }
      }

      return results;
    },
    [memoizedFilters, clienteId, orderedIdsObj, oldOrder]
  );

  const mapaMinaRequest = useGetMapaMina(clienteId, forceRefetch);

  const initializeSearchs = async () => {
    const modifiedData = [];
    await db.mapaDaMina
      .filter((e) => e.clienteId == clienteId && (e.updated || e.new || e.deletedAt))
      .each((e) => modifiedData.push(e));

    const data = [];
    await db.mapaDaMinaOriginal.filter((e) => e.clienteId == clienteId).each((e) => data.push(e));

    await resetMapa({ data, table: db.mapaDaMina, clienteId });

    if (modifiedData.length > 0) {
      for (const item of modifiedData) {
        await db.mapaDaMina.put(item);
      }
    }

    setFreeToInitialize(true);
  };

  const countMapaMina = async () => await db.mapaDaMina.where("clienteId").equals(clienteId).count();

  useEffect(() => {
    (async () => {
      const count = await countMapaMina();

      if (updateMapaData || count == 0) {
        initializeSearchs();
      } else {
        setFreeToInitialize(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (isOnline) {
      if (mapaMinaRequest.data?.mapasMina) {
        setSearchedCount((prev) => prev + 1);

        // Evita loops criando uma função assíncrona separada
        const processData = async () => {
          await updateNaoIds({ historyIds: mapaMinaRequest.data.historyIds, clienteId });
          const count = await countMapaMina();
          await updateData({
            data: mapaMinaRequest.data.mapasMina,
            resetMapaDaMina: count == 0 && updateMapaData,
            clienteId,
          });
          const finalCount = await countMapaMina();
          if (finalCount == 0) {
            initializeSearchs();
          }
          setLoadingMapaRequest(false);
        };

        processData();
      } else if (mapaMinaRequest.data && !mapaMinaRequest.data?.mapasMina) {
        setLoadingMapaRequest(mapaMinaRequest.isLoading);
      }
    } else {
      setSearchedCount((prev) => prev + 1);
      setLoadingMapaRequest(false);
    }
  }, [mapaMinaRequest.data, isOnline, updateMapaData, clienteId]);

  return {
    setOrderedIdsObj,
    searchedCount,
    loadingMapaRequest,
    updateData,
    mapasMina,
    mapasMinaOriginais,
    updateNaoIds,
  };
};

export default useMapaMina;
